import { useMemo } from 'react';
import {
  configApiRef,
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { Combobox, type Option } from '@backstage/ui';
import { fetchSnykOrgs } from 'shared-react';
import useAsync from 'react-use/esm/useAsync';

/**
 * A live dropdown of the Snyk orgs in the configured group - queried from
 * Snyk itself, not the Backstage catalog, since no Snyk-org catalog entity
 * exists. The request, pagination and attribute mapping are shared-react's
 * fetchSnykOrgs - the same typed loader plugins/snyk-branch-view's
 * useSnykOrgs hook calls - so this component owns only the Combobox
 * rendering; see SortedEntityField for the Combobox pattern this follows,
 * just fetching from Snyk instead of the catalog.
 *
 * Emits the org's bare id (`snyk_org_id`), not a composite ref - every
 * workflow task this feeds expects exactly that string.
 */
export const SnykOrgField = (props: FieldExtensionComponentProps<string>) => {
  const { onChange, formData, schema, required, rawErrors } = props;
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const groupId = configApi.getOptionalString('snyk.groupId');

  const {
    value: orgs,
    loading,
    error,
  } = useAsync(async () => {
    if (!groupId) {
      throw new Error('snyk.groupId is not configured');
    }
    return fetchSnykOrgs(groupId, { fetchApi, discoveryApi });
  }, [groupId]);

  const options: Option[] = useMemo(() => {
    if (loading) {
      return [{ id: '__loading__', label: 'Loading…', disabled: true }];
    }
    if (error) {
      return [
        { id: '__error__', label: `Error: ${error.message}`, disabled: true },
      ];
    }
    return (orgs ?? []).map(org => ({ id: org.id, label: org.name }));
  }, [orgs, loading, error]);

  return (
    <Combobox
      label={(schema.title as string) ?? 'Snyk Org'}
      description={schema.description as string | undefined}
      isRequired={required}
      isInvalid={Boolean(rawErrors?.length)}
      options={options}
      selectedKey={formData ?? null}
      onSelectionChange={key => onChange(key ? String(key) : undefined)}
    />
  );
};
